import { ClientGroupMembershipStatusEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Collapsible from 'react-native-collapsible';
import { Text, TouchableRipple } from 'react-native-paper';

import { DownIcon } from '@/assets';
import { useGetGroups } from '@/features/api/hooks/grapevine-group/useGetGroups';
import type { PostFeedTypeFeature } from '@/features/feature-management/types/postTypeFeature';
import { PostTypeSelectionView } from '@/features/post-editor/components/PostTypeSelectionView';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import { getSenderChannelDisplayName } from '@/features/post-editor/utils/postCreationOptions';
import { resolveChannelLabel } from '@/features/post-editor/utils/resolveChannelLabel';
import { PostFeedType } from '@/features/posts/types/postFeed';
import { POST_FEED_TYPE_ICONS } from '@/features/posts/utils/postFeed';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { RotatableView } from '@/shared/components/animated/RotatableView';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type ChannelSelectionProps = {
  readonly?: boolean;
  channelType?: PostFeedType;
  groupId?: string;
  isOrganization?: boolean;
  senderChannelOptions?: PostFeedTypeFeature[];
  onChannelTypeSelect: (value: PostFeedType) => void;
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: 0,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    headerRow: {
      height: 48,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
      paddingVertical: 8,
      paddingHorizontal: 16,
      backgroundColor: theme.colors.tertiaryContainer
    },
    title: {
      marginRight: 'auto',
      color: theme.colors.primary
    },
    collapsible: {
      backgroundColor: theme.colors.surfaceContainerLowest
    }
  });

export const ChannelSelection: FC<ChannelSelectionProps> = ({
  readonly = false,
  channelType = PostFeedType.GOSSIP,
  groupId,
  isOrganization = false,
  senderChannelOptions,
  onChannelTypeSelect
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { t: tPostType } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'types'
  });
  const [expanded, setExpanded] = useState(false);

  const postTypeFeature = useGetPostTypeFeature(channelType);
  const channelName =
    readonly && isOrganization && senderChannelOptions === undefined
      ? postTypeFeature.channelName
      : getSenderChannelDisplayName(
          senderChannelOptions ?? [],
          channelType,
          tPostType(channelType)
        );

  const { data: groups } = useGetGroups();

  const myGroups = useMemo(
    () =>
      groups?.filter(
        ({ membershipStatus }) =>
          membershipStatus === ClientGroupMembershipStatusEnum.Approved
      ),
    [groups]
  );

  const groupName = myGroups?.find((g) => g.id === groupId)?.name;

  // Todo: the check is not correct this way, fix it when my group section is implemented
  const isOpenedFromGroupFeed = !isEmptyOrNull(groupId);

  const handleExpandChange = useCallback(() => {
    if (isOpenedFromGroupFeed) return;
    setExpanded(!expanded);
  }, [expanded, isOpenedFromGroupFeed]);

  const handleChannelTypeChange = useCallback(
    (selectedChannelType: PostFeedType) => {
      setExpanded(false);
      onChannelTypeSelect(selectedChannelType);
    },
    [onChannelTypeSelect]
  );

  if (readonly) {
    return (
      <View style={styles.container}>
        <TouchableRipple accessibilityRole="button" disabled={readonly}>
          <View style={styles.headerRow}>
            {POST_FEED_TYPE_ICONS[channelType]({
              fill: theme.colors.primary,
              height: 24,
              width: 24
            }) ?? <></>}
            <Text variant="titleLarge" style={styles.title}>
              {groupName ??
                resolveChannelLabel(
                  channelType,
                  channelName,
                  isOrganization,
                  t
                )}
            </Text>
          </View>
        </TouchableRipple>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TouchableRipple
        testID="post-creation-channel-selector"
        accessibilityRole="button"
        onPress={handleExpandChange}
        disabled={isOpenedFromGroupFeed}
      >
        <View style={styles.headerRow}>
          {POST_FEED_TYPE_ICONS[channelType]({
            fill: theme.colors.primary,
            height: 24,
            width: 24
          }) ?? <></>}
          <Text variant="titleLarge" style={styles.title}>
            {groupName ??
              resolveChannelLabel(channelType, channelName, isOrganization, t)}
          </Text>
          {!isOpenedFromGroupFeed && (
            // TODO: implement "Meine Gruppen" section
            <RotatableView changeRotation={expanded}>
              <DownIcon width={32} height={32} fill={theme.colors.primary} />
            </RotatableView>
          )}
        </View>
      </TouchableRipple>
      <Collapsible
        collapsed={!expanded}
        collapsedHeight={0}
        renderChildrenCollapsed
        style={styles.collapsible}
      >
        <PostTypeSelectionView
          selected={channelType}
          channels={senderChannelOptions ?? []}
          isOrganization={isOrganization}
          onSelect={handleChannelTypeChange}
        />
      </Collapsible>
    </View>
  );
};
